type Setter<T> = (v: T) => void;
type Deleter = () => void;
type StoreCrud<T> = () => [T | null, Setter<T>, Deleter];

const makeStorage = <T,>(storage: Storage, key: string): StoreCrud<T> => {
	const get = (): T | null => {
		try {
			const raw = storage.getItem(key);
			if (raw === null) return null;
			return JSON.parse(raw) as T;
		} catch {
			return null;
		}
	};
	const set = (v: T): void => {
		try {
			storage.setItem(key, JSON.stringify(v));
		} catch {
			// silent fail (e.g. Safari private mode, quota exceeded)
		}
	};
	const remove = (): void => {
		try {
			storage.removeItem(key);
		} catch {
			// silent fail
		}
	};
	return () => [get(), set, remove];
};

const newSessionStorage = <T,>(key: string): StoreCrud<T> =>
	makeStorage<T>(window.sessionStorage, key);

const newLocalStorage = <T,>(key: string): StoreCrud<T> =>
	makeStorage<T>(window.localStorage, key);

/**
 * WebAuthn や 再認証 でどの Settings Method にいたかを記憶しておくためのストア
 */
export type PreviousSettingMethod = {
	flowId: string;
	method: string;
};
export const usePreviousSettingMethods: StoreCrud<PreviousSettingMethod> =
	newSessionStorage("PreviousSettingMethod");

/**
 * Register, Verify, の2つの Flow を完了させた後の遷移先。もしくは Organization Account を有効化した後の遷移先
 */
export type RegistrationReturnTo = {
	returnTo: string;
	expiresAt: number;
};
export const useRegistrationReturnTo: StoreCrud<RegistrationReturnTo> =
	newLocalStorage("RegistrationReturnTo");

export const expiresAfter = (hours: number): number => {
	const expiresAt = new Date();
	expiresAt.setHours(expiresAt.getHours() + hours);
	return expiresAt.getTime();
};
